import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { getEntries, getGameCodesAvailability } from "./Api";
import { selectMyListEntries } from "../Selectors/MylistSelectors";
import { myListActions } from "../Store/MylistSlice";
import { idbCache } from "./Caching";
import { remove } from "./StorageIDB";
import { Award, Entry as APIEntry, GetGameCodesAvailabilityResponse, AvailabilityPlatforms, EntryData } from "../api";
import { selectEntriesByAwardId } from "../Selectors/EntriesSelectors";
import { RootState } from "../Store/Store";
import { entriesActions } from "../Store/EntriesSlice";

export type PreparedEntry = ReturnType<typeof prepareEntry>

export type FilterData = {
    categoriesSelected?: number[],
    tagsSelected?: string[],
    watched?: "all"|"watched"|"unwatched",
    notes?: "saved"|"empty",
    searchQuery?: string,
}

export type Tag = string;

export type EntryTagsMap = Record<string, Tag[]>

export type EntrySortType = "a-z"|"z-a"|"date-added"|"runtime-ascending"|"runtime-descending"

export function useAwardEntries(award: Award) {
    const dispatch = useDispatch();
    const awardEntries = useSelector((state: RootState) => selectEntriesByAwardId(state, award?.awardId));

    useEffect(() => {
        (async () => {
            if(awardEntries || !award) return;

            const loadedEntries = (await getCachedAwardEntries(award.awardId))
                ?.map(entry => prepareEntry(entry, award))
            ;

            // TODO: show user error
            if(!loadedEntries) {
                console.error?.("getCachedAwardEntries returned an empty result.  The API call likely failed or returned an unexpected result.");
                return;
            }

            const entries = addHasCategoryPageToEntriesFromAwards(loadedEntries, [award]);
        
            dispatch(entriesActions.setEntries({ 
                entries: entries,
                awardId: award.awardId,
            }));
        })();
    }, [dispatch, award, awardEntries]);

    return awardEntries;
}

export function useMyListEntries(awards: Award[]) {
    const dispatch = useDispatch();
    const myListEntries = useSelector(selectMyListEntries);

    useEffect(() => {
        (async () => {
            if(!awards || myListEntries) return;

            const awardMap = Object.fromEntries(awards.map(award => [
                award.awardId,
                award,
            ]));

            const entries = (await getCachedMyListEntries())
                ?.map((entry) => {
                    const award = awardMap[""+entry.awardId];
                    if(!award) throw new Error(`award id ${entry.awardId} not found for entry (${entry.id}) ${entry.title}`);
                    return prepareEntry(entry, award)
                })
            ;

            // TODO: show user error
            if(!entries) {
                console.error?.("useMyListEntries returned an empty result.  The API call likely failed or returned an unexpected result.");
                return;
            }

            // fix data inconsistancy where an entry can have been added to my list and then 
            // the award has been changed whether it is a category award or not
            const normalisedEntries = entries.filter(entry => 
                (!entry.hasCategoryPage && entry.isInMyList) || 
                (entry.hasCategoryPage && Boolean(Object.entries(entry.categories).find(([id, cat]) => cat.isInMyList)))
            )
            
            //setMyListEntries(cachedMyList);
            dispatch(myListActions.setEntries({ entries: normalisedEntries }));
        })();
    }, [dispatch, awards, myListEntries]);

    return myListEntries;
}

export function useGameAvailability(awardId: string) {
    const [gameAvailability, setGameAvailability] = useState<GetGameCodesAvailabilityResponse|null>(null);

    useEffect(() => {
        (async () => {
            if(!awardId) return;

            const availablity = await getGameCodesAvailability(awardId)

            setGameAvailability(availablity ?? null);
        })();
    }, [awardId]);

    return gameAvailability;
}

export function getEntryAvailabilityTagsMap(gameAvailabilityPlatforms: AvailabilityPlatforms) {
    return !gameAvailabilityPlatforms
        ? null 
        : Object.entries(gameAvailabilityPlatforms)
            .reduce((map, [tagName, entryIds]) => {
                entryIds.forEach(entryId => {
                    if(!map[entryId]) map[entryId] = [];

                    map[entryId].push(tagName);
                });

                return map;
            }, {} as EntryTagsMap)
    ;
}

export function formatCacheKeyMyList() {
    return 'entries-mylist';
}

export async function getCachedAwardEntries(awardId: string) {
    const cachedEntriesResult = await idbCache(
        formatCacheKeyAwardEntries(awardId), 
        60 * 10, 
        () => getEntries(awardId),
        validateEntriesResponse
    );

    return validateEntriesResponse(cachedEntriesResult) ? cachedEntriesResult.entries : null;
}

export function formatCacheKeyAwardEntries(awardId: string) {
    return `entries-award${awardId}`;
}

export async function setCachedAwardEntries(awardId: string, entries: Array<APIEntry|PreparedEntry>) {
    return await idbCache(
        formatCacheKeyAwardEntries(awardId), 
        60 * 10,
        () => ({
            status: 'OK',
            entries,
        }),
        () => false,
    );
}

export async function removeCacheAwardEntries(awardId: string) {
    return await remove(formatCacheKeyAwardEntries(awardId));
}

export async function getCachedMyListEntries() {
    const cachedEntriesResult = await idbCache(
        formatCacheKeyMyList(),  
        60 * 10, 
        () => getEntries(null, true),
        validateEntriesResponse
    );

    return validateEntriesResponse(cachedEntriesResult) ? cachedEntriesResult.entries : null;
}

export async function setCachedMyListEntries(entries: Array<APIEntry|PreparedEntry>) {
    return await idbCache(
        formatCacheKeyMyList(), 
        60 * 10,
        () => ({
            status: 'OK',
            entries,
        }),
        () => false,
    );
}

export async function removeCacheMyListEntries() {
    return await remove(formatCacheKeyMyList());
}

function validateEntriesResponse<TEntry>(response?: Record<string, any>): response is {
    status: "OK",
    entries: Array<TEntry>,
} {
    return response?.status === "OK" && response?.entries && Array.isArray(response.entries);
}

export function prepareEntry(entry: APIEntry, award: Award) {
    /* Prepare and format any data to be ready for printing */

    const datePublishingEnd = entry.publishingEndDate ? new Date(entry.publishingEndDate) : null;
    const datePublishedDate = typeof entry?.data?.publishedDate?.answer === "string" ? new Date(entry?.data?.publishedDate?.answer) : null;
    
    return {
        ...entry,

        primaryImage: entry.data?.primaryEntryImage?.thumbnailUrl ?? '',
        dateAdded: entry.data?.metadataField1?.answer?.toString() ?? null,
        certification: entry.data?.certification ?? null,
        genre: entry.data?.metadataField2?.answer?.toString() ?? null,
        runTime: entry.data?.metadataField3?.answer ? Number(entry.data?.metadataField3?.answer) : null,
        synopsis: entry.data?.synopsis?.answer?.toString() ?? null,
        gameBundles: entry.data?.gameBundles?.answer?.toString() ?? null,

        isFreeToPlay: entry.data?.freeToPlay?.answer === true,
        freeToPlayMessage: entry.data?.freeToPlayDetails?.answer?.toString(),

        hasCategoryPage: award?.hasCategoryPage,

        datePublishingEndString: isValidDate(datePublishingEnd) ? datePublishingEnd?.toISOString() : null,
        datePublishingEndSeconds: isValidDate(datePublishingEnd) ? datePublishingEnd.getTime() / 1000 : null,

        datePublishedDateString: isValidDate(datePublishedDate) ? datePublishedDate?.toISOString() : null,
        datePublishedDateSeconds: isValidDate(datePublishedDate) ? datePublishedDate.getTime() / 1000 : null,
    }
};


function isValidDate(date: Date|null): date is NonNullable<Date> {
    if(!date) return false;

    return !Number.isNaN(Number(date?.getTime()));
}

export function entryIsWatchedFor(entry: PreparedEntry, categoryId: string) {
    return !entry.hasCategoryPage 
        ? entry.isWatched
        : entry.categories[categoryId]?.isWatched
    ;
}

export function entryIsInMyListFor(entry: PreparedEntry, categoryId: string) {
    return !entry.hasCategoryPage 
        ? entry.isInMyList
        : entry.categories[categoryId]?.isInMyList
    ;
}


export function entryIsInMyList<TEntry extends APIEntry>(entry: TEntry) {
    return entry.isInMyList 
        || (entry.hasCategoryPage && Object.values(entry.categories).some(category => category.isInMyList))
    ;
}

export const filterEntries = (entries: PreparedEntry[], filters: FilterData, entryAdditionalTagsMap: EntryTagsMap|null = null) => {
    const {
        categoriesSelected,
        tagsSelected,
        watched,
        notes,
        searchQuery,
    } = filters;

    const filteredEntries = entries.filter(entry => {

        if(watched !== 'all') {
            if (watched === `watched` && !entry.isWatched) {
                return false
            }
            if (watched === `unwatched` && entry.isWatched) {
                return false
            }
        }

        if (notes === `saved` && !entry.hasMemberNote) {
            return false
        }

        if (notes === `empty` && entry.hasMemberNote) {
            return false
        }

        if (searchQuery && searchQuery.trim() && entry.title.toLowerCase().indexOf(searchQuery.trim().toLowerCase()) === -1) {
            return false
        }

        if (tagsSelected?.length) {
            const hasAtLeastOneSelectedTag = [...entry.tags, ...(entryAdditionalTagsMap?.[entry.id] ?? [])]
                // .map(tag => tag.replace(/\s/g, '').toUpperCase())
                .map(tag => tag.toUpperCase())
                .some(tag => tagsSelected.includes(tag));
            
            if(!hasAtLeastOneSelectedTag) {
                return false;
            }
        }


        if (categoriesSelected?.length) {
            const hasAtLeastOneSelectedCategory = Object.values(entry.categories)
                .some(category => categoriesSelected.includes(parseInt(category.id)));
            
            if(!hasAtLeastOneSelectedCategory) {
                return false;
            }
        }

        return true;
    });

    return filteredEntries;
}

export const sortEntries = (entries: PreparedEntry[], sortType: EntrySortType) => {

    const removeArticles = (str: string) => {
        let words = str.split(" ");
        if (words.length <= 1) return str;
        if (words[0] === 'a' || words[0] === 'the' || words[0] === 'an')
            return words.splice(1).join(" ");
        return str;
    }

    switch (sortType) {
        case "a-z":
            entries = entries.sort((a, b) => {
                let titleA = a.title.toLowerCase(),
                    titleB = b.title.toLowerCase();

                titleA = removeArticles(titleA).replace(/\s/g, '');
                titleB = removeArticles(titleB).replace(/\s/g, '');
                return titleA.localeCompare(titleB);
            });
            break;
        case "z-a":
            entries = entries.sort((a, b) => {
                let titleA = a.title.toLowerCase(),
                    titleB = b.title.toLowerCase();

                titleA = removeArticles(titleA).replace(/\s/g, '');
                titleB = removeArticles(titleB).replace(/\s/g, '');

                return titleB.localeCompare(titleA);
            })
            break;
        case "date-added":
            // Sort by publishedDate descending.
            entries = entries.sort((a, b) => {
                const aDate = a.datePublishedDateSeconds ?? 0;
                const bDate = b.datePublishedDateSeconds ?? 0;
                return bDate - aDate;
            });
            break;
        case "runtime-ascending":
            entries = entries.sort((a, b) => (a.runTime ?? 0) - (b.runTime ?? 0))
            break;
        case "runtime-descending":
            entries = entries.sort((a, b) => (b.runTime ?? 0) - (a.runTime ?? 0))
            break;
        default:

    }

    return entries;
}

// get the available data from the data object that is in the format:
// { additional<type><#>: {}, ...}
// where <#> is not sequential and numbers may or may not exist, so we can't simply get the fields in order
export function getAvailableDataItems(allData: EntryData, baseKeyName: string) {
    return Object.keys(allData)
        // get the object property key names that begin with the base key
        .filter(dataKey => (new RegExp(`^${baseKeyName}[0-9]+`)).test(dataKey))
        // return the objects of for the selected keys
        .map(foundKey => {
            const keyIdMatch = foundKey.match(/[0-9]+$/);
            return {
                ...(allData[foundKey]),
                // add the key into the object so we know the field the item came from
                key: foundKey,
                keyId: keyIdMatch ? keyIdMatch[0] : null,
            }
        });
}

export function getMetadataItems(allData: EntryData, thirdItemIsDuration = true, hasRuntime = false) {
    return getAvailableDataItems(allData, "metadataField")
        .map(item => {
            if(item.key === "metadataField3" && thirdItemIsDuration && hasRuntime
                && item.answer && typeof item.answer === "string" 
                && parseFloat(item.answer)) {
                return {
                    ...item,
                    answer: formatSecondsAsDuration(parseFloat(item.answer)),
                };
            }
            return item;
        });
}

export function formatSecondsAsDuration(totalSeconds: number) {
    return `${Math.round(totalSeconds / 60)}m`;
}

export function addHasCategoryPage(entry: PreparedEntry, hasCategoryPage: boolean) {
    return {
        ...entry, 
        hasCategoryPage
    };
}
// export function addHasCategoryPageToEntries(entries, hasCategoryPage) {
//     const newEntries = entries?.map((entry) => addHasCategoryPage(entry, hasCategoryPage));

//     return newEntries;
// }
export function addHasCategoryPageToEntriesFromAwards(entries: PreparedEntry[], awards: Award[]) {
    const awardMap = Object.fromEntries(awards.map(award => [
        ""+award.awardId,
        award,
    ]));
    const newEntries = entries?.map(entry => {
        const award = awardMap[""+entry.awardId];
        if(!award) throw new Error(`award id ${entry.awardId} not found for entry (${entry.id}) ${entry.title}`);
        return addHasCategoryPage(entry, award.hasCategoryPage);
    });
    return newEntries;
}

export function getTagsFromEntries (entries: PreparedEntry[]) {
    const tagsFromEntries = entries?.map((entry) => {
        return entry.tags.map(tagString => {
            return {id: tagString.toUpperCase(), title: tagString.replace(/,/g, '')};
        });
    })
    const allTags = tagsFromEntries.flat();
    const uniqueTags = allTags
        ?.filter(
            (tag, i, allTags) => 
                // if the index of this items ID doesn't match the first index of the that ID in the full list, it is a duplicate
                allTags.findIndex(allTag => (allTag.id === tag.id)) === i
        );
    const sortedTags = uniqueTags?.sort((a, b) => a.title.localeCompare(b.title))
    return sortedTags;
}

export function getCategoriesFromEntries(entries: PreparedEntry[]) {
    const categoriesFromEntries = entries?.map((entry) => {
        return Object.values(entry.categories);
    });
    const allCategories = categoriesFromEntries?.flat();
    const uniqueCategories = allCategories
        ?.filter(
            (category, i, allCategories) => 
                // if the index of this items ID doesn't match the first index of the that ID in the full list, it is a duplicate
                allCategories.findIndex(allCategory => (allCategory.id === category.id)) === i
        );
    const sortedCategories = uniqueCategories?.sort((a, b) => a.title.localeCompare(b.title))
    return sortedCategories;
}

export async function mergeCachedAwardEntry(awardId: string, newEntry: PreparedEntry) {
    if(!newEntry.id) {
        throw Error("'newEntry.id' must be provided to mergeCachedEntry");
    }

    // Load entries
    const currentEntries = await getCachedAwardEntries(awardId);
    if(!currentEntries) throw new Error("mergeCachedAwardEntry: getCachedAwardEntries didn't return a result.  The API call likely failed.");

    // Update our entry
    const newEntries = currentEntries.map(oldEntry => {
        if(newEntry.id === oldEntry.id) {
            return {
                ...oldEntry,
                ...newEntry,
            }
        }
        return oldEntry;
    });
    // Save entries
    setCachedAwardEntries(awardId, newEntries);
}
export async function setCachedMyList(newEntry: PreparedEntry) {
    if(!newEntry.id) throw Error("'newEntry.id' must be provided to setCachedMyList");
    if(typeof newEntry.isInMyList === 'undefined') throw Error("'newEntry.isInMyList' must be provided to setCachedMyList");

    // Load entries
    const currentMyList = await getCachedMyListEntries();
    if(!currentMyList) throw new Error("setCachedMyList: getCachedMyListEntries didn't return a result.  The API call likely failed.");
    // Update our entry
    const newMyList = addUpdateOrRemoveMyListEntry(currentMyList, newEntry);

    // Save entries
    setCachedMyListEntries(newMyList);
}

export function addUpdateOrRemoveMyListEntry<TEntry extends APIEntry>(currentMyList: TEntry[], newEntry: PreparedEntry) {
    return (newEntry.isInMyList && !(currentMyList.find(entry => entry.id === newEntry.id)))
        // if we're adding to the list and it isn't in the list then add it
        ? [...currentMyList, newEntry]
        // in all other cases we find the entry in the list and update it, then filter out any that are being removed
        : currentMyList
            // update the entry
            .map(oldEntry => {
                if(newEntry.id === oldEntry.id) {
                    return {
                        ...oldEntry,
                        ...newEntry,
                    }
                }
                return oldEntry;
            })
            // remove from my list if it isn't in there
            .filter(entry => entryIsInMyList(entry));
}

